import { c as _c } from "react/compiler-runtime";
import { type ReactNode, useEffect } from 'react';
import { create } from 'zustand';
export type DirtyData = {
  /** List of component IDs that have unsaved changes */
  dirtyComponents: string[];
  /** Component IDs with an open sub-editor, which must save or cancel before the panel's Save */
  blockingComponents: string[];
};
type DirtyStore = {
  data: DirtyData;
  set: (data: Partial<DirtyData>) => void;
};
const initialDirtyState: DirtyData = {
  dirtyComponents: [],
  blockingComponents: []
};
export const useDirtyStore = create<DirtyStore>()(zustandSet => ({
  data: initialDirtyState,
  set: (partialData: Partial<DirtyData>) => zustandSet(state => ({
    data: {
      ...state.data,
      ...partialData
    }
  }))
}));

// Provider component for testing - allows setting initial store state
export const DirtyStoreProvider = t0 => {
  const $ = _c(5);
  const {
    children,
    initialData
  } = t0;
  let t1;
  let t2;
  if ($[0] !== initialData) {
    t1 = () => {
      if (initialData) {
        useDirtyStore.getState().set(initialData);
      }
    };
    t2 = [initialData];
    $[0] = initialData;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] !== children) {
    t3 = <>{children}</>;
    $[3] = children;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
};
const useDirty = () => {
  const $ = _c(5);
  const {
    data,
    set
  } = useDirtyStore();
  const addDirtyComponent = _temp;
  const removeDirtyComponent = _temp2;
  const toggle = _temp3;
  const toggleBlocking = _temp4;
  const clearDirtyComponents = _temp5;
  const t0 = data.dirtyComponents.length > 0;
  const t1 = data.blockingComponents.length > 0;
  let t2;
  if ($[0] !== data || $[1] !== set || $[2] !== t0 || $[3] !== t1) {
    t2 = {
      data,
      set,
      addDirtyComponent,
      removeDirtyComponent,
      clearDirtyComponents,
      isAnyComponentDirty: t0,
      isAnyComponentBlocking: t1,
      toggle,
      toggleBlocking
    };
    $[0] = data;
    $[1] = set;
    $[2] = t0;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};
export default useDirty;
export { initialDirtyState };
function _temp(component) {
  const currentDirtyComponents = useDirtyStore.getState().data.dirtyComponents;
  if (!currentDirtyComponents.includes(component)) {
    useDirtyStore.getState().set({
      dirtyComponents: [...currentDirtyComponents, component]
    });
  }
}
function _temp2(component_0) {
  const currentDirtyComponents_0 = useDirtyStore.getState().data.dirtyComponents;
  if (currentDirtyComponents_0.includes(component_0)) {
    useDirtyStore.getState().set({
      dirtyComponents: currentDirtyComponents_0.filter(dc => dc !== component_0)
    });
  }
}
function _temp3(component_1, isDirty) {
  const currentDirtyComponents_1 = useDirtyStore.getState().data.dirtyComponents;
  const isCurrentlyDirty = currentDirtyComponents_1.includes(component_1);
  if (isDirty && !isCurrentlyDirty) {
    useDirtyStore.getState().set({
      dirtyComponents: [...currentDirtyComponents_1, component_1]
    });
  } else {
    if (!isDirty && isCurrentlyDirty) {
      useDirtyStore.getState().set({
        dirtyComponents: currentDirtyComponents_1.filter(dc_0 => dc_0 !== component_1)
      });
    }
  }
}
function _temp4(component_2, isBlocking) {
  const currentBlockingComponents = useDirtyStore.getState().data.blockingComponents;
  const isCurrentlyBlocking = currentBlockingComponents.includes(component_2);
  if (isBlocking && !isCurrentlyBlocking) {
    useDirtyStore.getState().set({
      blockingComponents: [...currentBlockingComponents, component_2]
    });
  } else {
    if (!isBlocking && isCurrentlyBlocking) {
      useDirtyStore.getState().set({
        blockingComponents: currentBlockingComponents.filter(bc => bc !== component_2)
      });
    }
  }
}
function _temp5() {
  useDirtyStore.getState().set({
    dirtyComponents: [],
    blockingComponents: []
  });
}